class VariantPriceCalculator {
  constructor(variantsArray) {
    this.variantsArray = Array.isArray(variantsArray) ? variantsArray : [];
  }
  getUnitPrice(selectedWidth) {
    const v = this.variantsArray.find(v2 => String(v2.title).trim() === String(selectedWidth).trim());
    const price = v?.price;
    const n = typeof price === "string" ? parseFloat(price) : Number(price);
    return Number.isFinite(n) ? n : 0;
  }
}

class DOMUpdater {
  constructor(calculatedAreaEl, calculatedPriceEl, currencySymbol) {
    this.calculatedAreaEl = calculatedAreaEl;
    this.calculatedPriceEl = calculatedPriceEl;
    this.currencySymbol = currencySymbol || "£";
  }
  updateArea(area) {
    if (!this.calculatedAreaEl) return;
    const val = Number.isFinite(area) ? area : 0;
    this.calculatedAreaEl.textContent = `${val.toFixed(2)} m\u00B2`;
  }
  updatePrice(price) {
    if (!this.calculatedPriceEl) return;
    const val = Number.isFinite(price) ? price : 0;
    this.calculatedPriceEl.textContent = `${this.currencySymbol}${val.toFixed(2)}`;
  }
  reset() {
    this.updateArea(0);
    this.updatePrice(0);
  }
}

class SizingCalculator extends HTMLElement {
  constructor() {
    super();

    // --- Load variants: prefer data-variant-json, then JSON-LD offers ---
    let offers = [];

    // 1) data-variant-json (commonly Shopify exposes variants here)
    try {
      const varJson = document.querySelector("script[data-variant-json]");
      if (varJson?.textContent) {
        const parsed = JSON.parse(varJson.textContent);
        const arr = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.variants) ? parsed.variants : []);
        offers = arr.map(v => ({
          title: v?.title ?? v?.name ?? v?.sku ?? "",
          price: v?.price ?? v?.price_amount ?? v?.priceV2?.amount
        }));
      }
    } catch { /* ignore */ }

    // 2) Fallback to JSON-LD offers
    if (!offers.length) {
      try {
        const ld = document.querySelector('script[type="application/ld+json"]');
        if (ld?.textContent) {
          const json = JSON.parse(ld.textContent);
          const raw = Array.isArray(json?.offers) ? json.offers : (json?.offers ? [json.offers] : []);
          offers = raw.map(o => ({
            title: o?.name ?? o?.sku ?? "",
            price: o?.price
          }));
        }
      } catch { /* ignore */ }
    }

    this.variantsArray = offers;
    this.priceCalculator = new VariantPriceCalculator(this.variantsArray);

    // Cached refs filled in connectedCallback
    this.widthInput = null;
    this.lengthInput = null;
    this.domUpdater = null;

    // A tiny debounce to smooth rapid input on mobile
    this._debounceTimer = null;
  }

  connectedCallback() {
    const currencySymbol = this.getAttribute("data-current-currency-symbol") || "£";
    this.widthInput = this.querySelector(".width-input");
    this.lengthInput = this.querySelector("#length-input");
    const calculatedPrice = this.querySelector("#calculated-price");
    const calculatedArea = this.querySelector("#calculated-area");

    this.domUpdater = new DOMUpdater(calculatedArea, calculatedPrice, currencySymbol);

    if (this.lengthInput) this.lengthInput.required = true;
    if (this.widthInput) this.widthInput.required = true;
    this.bindEvents();
    // Initial compute after first paint
    requestAnimationFrame(() => this.computeAndRender({ roundLength: false }));
  }

  bindEvents() {
    const { widthInput, lengthInput } = this;
    if (!widthInput || !lengthInput) return;

    // Width changes (select/radios) – recalc immediately
    widthInput.addEventListener("change", () => this.computeAndRender({ roundLength: false }));
    widthInput.addEventListener("input", () => this.computeAndRender({ roundLength: false }));

    // Length typing – recalc live but DO NOT round while typing
    lengthInput.addEventListener("input", () => {
      // Debounce to avoid thrashing on mobile keyboards
      this.computeAndRender({ roundLength: false });
    });

    // Length change/blur (e.g., pressing "Done" on mobile) – clamp & round to next 0.5
    const finalizeLength = () => {
      this._clampAndRoundLength();
      this.computeAndRender({ roundLength: false });
    };
    lengthInput.addEventListener("change", finalizeLength);
    lengthInput.addEventListener("blur", finalizeLength);

    // Pressing Enter should also finalize on some devices
    lengthInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        lengthInput.blur();
      }
    });

    // As a safety net if something mounts late
    document.addEventListener("DOMContentLoaded", () => this.computeAndRender({ roundLength: false }));
  }

  // Clamp to min and round to the next 0.5 on finalize (not while typing)
  _clampAndRoundLength() {
    if (!this.lengthInput) return;
    const minAttr = this.lengthInput.getAttribute("min");
    const min = Number.isFinite(parseFloat(minAttr)) ? parseFloat(minAttr) : 0;

    let len = parseFloat((this.lengthInput.value || "").replace(",", "."));
    if (!Number.isFinite(len)) return;

    if (len < min) len = min;

    // Round to next 0.5
    len = Math.ceil(len * 2 - 1e-9) / 2;
    // Keep 1 decimal place to show .0 or .5
    this.lengthInput.value = len.toFixed(1);
  }

  computeAndRender({ roundLength = false } = {}) {
    const { widthInput, lengthInput, domUpdater } = this;
    if (!widthInput || !lengthInput || !domUpdater) return;

    // Parse inputs
    const width = parseFloat((widthInput.value || "").replace(",", "."));
    let length = parseFloat((lengthInput.value || "").replace(",", "."));

    if (!Number.isFinite(width) || !Number.isFinite(length) || width <= 0 || length <= 0) {
      domUpdater.reset();
      this.setPurchaseState(false);
      return false;
    }

    // If explicitly asked to round (we actually round on blur/change already)
    if (roundLength) {
      length = Math.ceil(length * 2 - 1e-9) / 2;
      lengthInput.value = length.toFixed(1);
    }

    // Effective unit price:
    // Your original math (unitPrice / (unitPrice / per_meter)) == per_meter.
    // So if data-per-meter is present & numeric, we use that as the unit price.
    const perMeterAttr = parseFloat(this.getAttribute("data-per-meter"));
    const hasPerMeter = Number.isFinite(perMeterAttr);
    const variantUnitPrice = this.priceCalculator.getUnitPrice(widthInput.value);
    const unitPrice = hasPerMeter ? perMeterAttr : variantUnitPrice;

    // Compute area & price
    const totalArea = width * length; // m²
    const totalPrice = totalArea * (Number.isFinite(unitPrice) ? unitPrice : 0);
    if (!Number.isFinite(totalPrice) || totalPrice <= 0) {
      domUpdater.reset();
      this.setPurchaseState(false);
      return false;
    }

    // Update the “manual cut” counter if present
    const dynamicManualCut = this.querySelector(".dynamic-manual-cut");
    if (dynamicManualCut) {
      // If you intend quantity to mirror area:
      dynamicManualCut.textContent = totalArea;
    }

    domUpdater.updateArea(totalArea);
    domUpdater.updatePrice(totalPrice);

    // Reflect into any line item properties if those inputs exist
    this.updateLineItemProperties(length, width, totalArea);
    this.setPurchaseState(true);
    return true;
  }

  setPurchaseState(valid) {
    const scope = this.closest('[data-section-type="product"]') || document;
    scope.querySelectorAll('.product-form__custom-and-buy').forEach(button => {
      button.disabled = !valid || button.dataset.sgPending === 'true';
      button.setAttribute('aria-disabled', String(button.disabled));
    });
    if (!valid) {
      scope.querySelectorAll('[name="properties[Width]"], [name="properties[Length]"], [name="properties[Total area]"]').forEach(input => {
        input.value = '';
        input.setAttribute('value', '');
      });
      const manualCut = scope.querySelector('.dynamic-manual-cut');
      if (manualCut) manualCut.textContent = '0';
    }
  }

  validateSelection() {
    this._clampAndRoundLength();
    const valid = this.computeAndRender();
    if (!valid && this.lengthInput) this.lengthInput.reportValidity();
    return valid;
  }

  updateQuantityInputs(unitsRequired) {
    const q1 = document.querySelector(".product-form__quantity input");
    if (q1) q1.value = `${unitsRequired}`;
    const q2 = document.querySelector(".product-form__quantity-and-buy-buttons-wrapper input.quantity-input__input");
    if (q2) q2.value = `${unitsRequired}`;
  }

  updateLineItemProperties(length, width, totalArea) {
    const setVal = (sel, val) => {
      const scope = this.closest('[data-section-type="product"]') || this;
      scope.querySelectorAll(sel).forEach(el => { el.value = val; el.setAttribute("value", val); });
    };

    setVal('.product__custom-option-type--text input[aria-label="Width"]', width);
    setVal('.product__custom-option-type--text input[aria-label="Length"]', length);
    setVal("[name='properties[Width]']", `${width} m`);
    setVal("[name='properties[Length]']", `${length} m`);
    setVal('.product__custom-option-type--text input[aria-label="Total area"]', `${totalArea.toFixed(2)} m\u00B2`);
    setVal("[name='properties[Total area]']", `${totalArea.toFixed(2)} m\u00B2`);
  }
}

customElements.define("sizing-calculator", SizingCalculator);
//# sourceMappingURL=/s/files/1/0895/7306/5053/t/4/assets/sizing-calculator.js.map


document.addEventListener('click', event => {
  const button = event.target.closest('.product-form__custom-and-buy');
  if (!button) return;
  const scope = button.closest('[data-section-type="product"]') || document;
  const calculator = scope.querySelector('sizing-calculator');
  if (!calculator || !calculator.validateSelection()) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);
